import React, { useState } from 'react';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';
import partnerApi from '../../api/partnerApi';
import { Loader2, Save, Lock } from 'lucide-react';
import { toast } from 'sonner';

export default function PartnerProfile() {
  const { partner, refreshPartner } = usePartnerAuth();

  const [profile, setProfile] = useState({
    first_name: partner?.first_name || '',
    last_name: partner?.last_name || '',
    phone: partner?.phone || '',
    whatsapp: partner?.whatsapp || '',
    business_name: partner?.business_name || '',
    city: partner?.city || '',
    state: partner?.state || '',
  });
  const [saving, setSaving] = useState(false);

  const [pwd, setPwd] = useState({ current: '', newPwd: '', confirm: '' });
  const [changingPwd, setChangingPwd] = useState(false);

  const updateProfile = (k, v) => setProfile(p => ({ ...p, [k]: v }));

  const saveProfile = async () => {
    setSaving(true);
    try {
      await partnerApi.put('/profile', profile);
      await refreshPartner();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (pwd.newPwd !== pwd.confirm) { toast.error('New passwords do not match'); return; }
    if (pwd.newPwd.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setChangingPwd(true);
    try {
      await partnerApi.post('/auth/change-password', {
        current_password: pwd.current,
        new_password: pwd.newPwd,
      });
      toast.success('Password changed successfully');
      setPwd({ current: '', newPwd: '', confirm: '' });
    } catch (err) {
      toast.error(err?.detail || 'Failed to change password');
    } finally {
      setChangingPwd(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-2xl mx-auto">
      <div>
        <h1 className="text-2xl font-outfit font-semibold text-slate-900">Profile Settings</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage your partner account details</p>
      </div>

      {/* Partner info card */}
      <div className="bg-[#EDF0FF] border border-[#516EFA]/20 rounded-2xl p-4 flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-[#516EFA] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
          {partner?.first_name?.[0]}{partner?.last_name?.[0]}
        </div>
        <div>
          <p className="font-semibold text-slate-900">{partner?.display_name}</p>
          <p className="text-xs text-slate-500">{partner?.email}</p>
          <p className="text-xs text-[#516EFA] font-medium mt-0.5 capitalize">
            {partner?.program_type} Partner &bull; Code: {partner?.partner_code}
          </p>
        </div>
      </div>

      {/* Profile form */}
      <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-5 space-y-4" data-testid="partner-profile-form">
        <p className="text-sm font-semibold text-slate-800 mb-1">Personal Information</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">First Name</label>
            <input value={profile.first_name} onChange={e => updateProfile('first_name', e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
              data-testid="profile-first-name"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Last Name</label>
            <input value={profile.last_name} onChange={e => updateProfile('last_name', e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
              data-testid="profile-last-name"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Phone</label>
          <input value={profile.phone} onChange={e => updateProfile('phone', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
            data-testid="profile-phone"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">WhatsApp</label>
          <input value={profile.whatsapp} onChange={e => updateProfile('whatsapp', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
            data-testid="profile-whatsapp"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Business Name</label>
          <input value={profile.business_name} onChange={e => updateProfile('business_name', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
            data-testid="profile-business"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">City</label>
            <input value={profile.city} onChange={e => updateProfile('city', e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">State</label>
            <input value={profile.state} onChange={e => updateProfile('state', e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
            />
          </div>
        </div>
        <button
          onClick={saveProfile}
          disabled={saving}
          className="flex items-center gap-2 bg-[#516EFA] hover:bg-[#425BE0] text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-50"
          data-testid="profile-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Password change */}
      <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-5 space-y-4" data-testid="partner-password-form">
        <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <Lock className="w-4 h-4 text-slate-400" /> Change Password
        </p>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Current Password</label>
          <input type="password" value={pwd.current} onChange={e => setPwd(p => ({ ...p, current: e.target.value }))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
            data-testid="profile-current-pwd"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">New Password</label>
          <input type="password" value={pwd.newPwd} onChange={e => setPwd(p => ({ ...p, newPwd: e.target.value }))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
            data-testid="profile-new-pwd"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Confirm New Password</label>
          <input type="password" value={pwd.confirm} onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
            data-testid="profile-confirm-pwd"
          />
        </div>
        <button
          onClick={changePassword}
          disabled={changingPwd || !pwd.current || !pwd.newPwd}
          className="flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-50"
          data-testid="profile-change-pwd-btn"
        >
          {changingPwd ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
          {changingPwd ? 'Updating...' : 'Update Password'}
        </button>
      </div>
    </div>
  );
}
